import { createFileRoute, notFound, Link } from '@tanstack/react-router'
import type { Product } from '@/types'
import { useState, useEffect } from 'react'
import { cartApi, wishlistApi, authTokenStorage, cartTokenStorage } from '@/lib/api'

export const Route = createFileRoute('/products/$slug')({
  component: ProductDetailPage,
  loader: async ({ params }) => {
    try {
      const response = await fetch('/data/products.json')
      const products: Product[] = await response.json()
      const product = products.find((p) => p.slug === params.slug)
      if (!product) throw notFound()
      return { product }
    } catch {
      throw notFound()
    }
  },
  notFoundComponent: () => (
    <div className="container mx-auto px-4 py-16 text-center">
      <h1 className="text-4xl font-bold mb-4">Product Not Found</h1>
      <Link to="/products" search={{}} className="text-blue-600 hover:underline">
        Browse all products
      </Link>
    </div>
  ),
})

function ProductDetailPage() {
  const { product } = Route.useLoaderData()
  const [quantity, setQuantity] = useState(1)
  const [selectedImage, setSelectedImage] = useState(0)
  const [adding, setAdding] = useState(false)
  const [addedToCart, setAddedToCart] = useState(false)
  const [inWishlist, setInWishlist] = useState(false)
  const [wishlistLoading, setWishlistLoading] = useState(false)
  const [isAuthenticated, setIsAuthenticated] = useState(false)

  const currencySymbol = '₹'

  useEffect(() => {
    const token = authTokenStorage.getAccessToken()
    setIsAuthenticated(!!token)

    // Check if product is in wishlist
    if (token) {
      checkWishlist()
    }
  }, [product.id])

  const checkWishlist = async () => {
    try {
      const wishlist = await wishlistApi.get() as { items: Array<{ product_id: number }> }
      setInWishlist(wishlist.items.some((item) => item.product_id === product.id))
    } catch {
      // Ignore errors
    }
  }

  const handleAddToCart = async () => {
    setAdding(true)
    try {
      const response = await cartApi.addItem(product.id, quantity) as { cart_token?: string }

      // Store cart token if returned
      if (response.cart_token) {
        cartTokenStorage.set(response.cart_token)
      }

      setAddedToCart(true)
      setTimeout(() => setAddedToCart(false), 3000)
    } catch (error) {
      console.error('Failed to add to cart:', error)
    } finally {
      setAdding(false)
    }
  }

  const handleToggleWishlist = async () => {
    if (!isAuthenticated) {
      // Redirect to login
      window.location.href = '/login'
      return
    }

    setWishlistLoading(true)
    try {
      if (inWishlist) {
        await wishlistApi.remove(product.id)
        setInWishlist(false)
      } else {
        await wishlistApi.add(product.id)
        setInWishlist(true)
      }
    } catch (error) {
      console.error('Failed to update wishlist:', error)
    } finally {
      setWishlistLoading(false)
    }
  }

  const extendedProduct = product as Product & {
    material?: string
    color?: string
    style?: string
    sku?: string
  }

  return (
    <div className="container mx-auto px-4 py-8">
      {/* Breadcrumb */}
      <nav className="flex items-center gap-2 text-sm text-gray-500 mb-6">
        <Link to="/" className="hover:text-gray-700">
          Home
        </Link>
        <span>/</span>
        <Link to="/products" search={{}} className="hover:text-gray-700">
          Products
        </Link>
        <span>/</span>
        <span className="text-gray-900">{product.name}</span>
      </nav>

      <div className="grid md:grid-cols-2 gap-12">
        {/* Images */}
        <div>
          {product.images && product.images.length > 0 ? (
            <>
              <div className="aspect-square bg-gray-100 rounded-lg overflow-hidden mb-4 relative">
                <img
                  src={product.images[selectedImage].url}
                  alt={product.images[selectedImage].alt_text || product.name}
                  className="w-full h-full object-cover"
                />

                {/* Sale badge */}
                {product.compare_at_price && product.compare_at_price > product.price && (
                  <span className="absolute top-4 left-4 bg-red-600 text-white px-3 py-1 rounded-full text-sm font-medium">
                    Sale
                  </span>
                )}
              </div>
              {product.images.length > 1 && (
                <div className="flex gap-2 overflow-x-auto">
                  {product.images.map((img, idx) => (
                    <button
                      key={img.id}
                      onClick={() => setSelectedImage(idx)}
                      className={
                        'w-20 h-20 flex-shrink-0 rounded-lg overflow-hidden border-2 transition-colors ' +
                        (idx === selectedImage ? 'border-blue-600' : 'border-gray-200 hover:border-gray-400')
                      }
                    >
                      <img src={img.url} alt="" className="w-full h-full object-cover" />
                    </button>
                  ))}
                </div>
              )}
            </>
          ) : (
            <div className="aspect-square bg-gray-100 rounded-lg flex items-center justify-center">
              <span className="text-gray-400">No image</span>
            </div>
          )}
        </div>

        {/* Details */}
        <div>
          <h1 className="text-3xl font-bold mb-4">{product.name}</h1>

          {/* SKU */}
          {extendedProduct.sku && (
            <p className="text-sm text-gray-500 mb-4">SKU: {extendedProduct.sku}</p>
          )}

          {/* Price */}
          <div className="flex items-baseline gap-3 mb-6">
            <span className="text-3xl font-bold">{currencySymbol}{product.price.toLocaleString()}</span>
            {product.compare_at_price && product.compare_at_price > product.price && (
              <>
                <span className="text-xl text-gray-500 line-through">
                  {currencySymbol}{product.compare_at_price.toLocaleString()}
                </span>
                <span className="bg-red-100 text-red-800 px-2 py-1 rounded text-sm font-medium">
                  Save {Math.round((1 - product.price / product.compare_at_price) * 100)}%
                </span>
              </>
            )}
          </div>

          {/* Description */}
          {product.description && <p className="text-gray-600 mb-6 leading-relaxed">{product.description}</p>}

          {/* Product attributes */}
          {(extendedProduct.material || extendedProduct.color || extendedProduct.style) && (
            <div className="grid grid-cols-2 gap-4 mb-6 p-4 bg-gray-50 rounded-lg">
              {extendedProduct.material && (
                <div>
                  <span className="text-sm text-gray-500">Material</span>
                  <p className="font-medium">{extendedProduct.material}</p>
                </div>
              )}
              {extendedProduct.color && (
                <div>
                  <span className="text-sm text-gray-500">Color</span>
                  <p className="font-medium">{extendedProduct.color}</p>
                </div>
              )}
              {extendedProduct.style && (
                <div>
                  <span className="text-sm text-gray-500">Style</span>
                  <p className="font-medium">{extendedProduct.style}</p>
                </div>
              )}
            </div>
          )}

          {/* Quantity & Add to Cart */}
          <div className="flex items-center gap-4 mb-4">
            <div className="flex items-center border rounded-lg">
              <button
                onClick={() => setQuantity(Math.max(1, quantity - 1))}
                disabled={!product.in_stock}
                className="px-4 py-3 hover:bg-gray-100 disabled:opacity-50"
              >
                -
              </button>
              <span className="px-4 py-3 border-x min-w-[50px] text-center">{quantity}</span>
              <button
                onClick={() => setQuantity(quantity + 1)}
                disabled={!product.in_stock}
                className="px-4 py-3 hover:bg-gray-100 disabled:opacity-50"
              >
                +
              </button>
            </div>

            <button
              onClick={handleAddToCart}
              disabled={!product.in_stock || adding}
              className="flex-1 bg-blue-600 text-white py-3 px-8 rounded-lg font-semibold hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors"
            >
              {adding ? 'Adding...' : addedToCart ? 'Added!' : !product.in_stock ? 'Out of Stock' : 'Add to Cart'}
            </button>

            {/* Wishlist button */}
            <button
              onClick={handleToggleWishlist}
              disabled={wishlistLoading}
              className={`p-3 rounded-lg border transition-colors ${
                inWishlist
                  ? 'bg-red-50 border-red-200 text-red-600'
                  : 'border-gray-300 text-gray-600 hover:border-gray-400'
              }`}
              title={inWishlist ? 'Remove from wishlist' : 'Add to wishlist'}
            >
              <svg
                className="w-6 h-6"
                fill={inWishlist ? 'currentColor' : 'none'}
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z"
                />
              </svg>
            </button>
          </div>

          {/* Added to cart notification */}
          {addedToCart && (
            <div className="mb-4 p-3 bg-green-50 border border-green-200 text-green-700 rounded-lg flex items-center gap-2">
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
              <span>Added to cart!</span>
              <Link to="/cart" className="ml-auto text-green-800 font-medium hover:underline">
                View Cart
              </Link>
            </div>
          )}

          {/* Stock status */}
          <div className="flex items-center gap-2 text-sm mb-6">
            {product.in_stock ? (
              <>
                <span className="w-2 h-2 bg-green-500 rounded-full"></span>
                <span className="text-green-700">In Stock</span>
              </>
            ) : (
              <>
                <span className="w-2 h-2 bg-red-500 rounded-full"></span>
                <span className="text-red-700">Out of Stock</span>
              </>
            )}
          </div>

          {/* Delivery info */}
          <div className="border-t pt-6 space-y-3">
            <div className="flex items-center gap-3 text-sm">
              <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M5 8h14M5 8a2 2 0 110-4h14a2 2 0 110 4M5 8v10a2 2 0 002 2h10a2 2 0 002-2V8m-9 4h4"
                />
              </svg>
              <span>Free delivery on orders over {currencySymbol}10,000</span>
            </div>
            <div className="flex items-center gap-3 text-sm">
              <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                />
              </svg>
              <span>Easy returns within 30 days</span>
            </div>
            <div className="flex items-center gap-3 text-sm">
              <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"
                />
              </svg>
              <span>Secure checkout</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
